import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { mobileStorage } from '@/utils/mobileStorage';

const API_URL = process.env.REACT_APP_API_URL || (Capacitor.isNativePlatform() ? 'https://pepitespot.fr/api' : '/api');

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Security fix E3/M4: always send httpOnly cookies
});

// Add auth interceptor
api.interceptors.request.use(async (config) => {
    // Security fix E3: On Web, tokens are sent via httpOnly cookies with withCredentials: true.
    // On Native Mobile (Capacitor), inject X-Client-Platform: mobile and Bearer token.
    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
        config.headers['X-Client-Platform'] = 'mobile';
        const authToken = await mobileStorage.getAuthToken();
        if (authToken && !config.headers['Authorization']) {
            config.headers['Authorization'] = `Bearer ${authToken}`;
        }
    }

    // Guest Mode: Only send X-Guest-ID when user is NOT authenticated
    // We track auth state via localStorage flag (migrated from sessionStorage for mobile sleep persistence)
    const isAuthenticated = localStorage.getItem('is_authenticated') === 'true';

    if (!isAuthenticated) {
        let guestId = isNative
            ? (await mobileStorage.getItem('guest_id')) || localStorage.getItem('guest_id')
            : localStorage.getItem('guest_id');
        if (!guestId) {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                guestId = crypto.randomUUID();
            } else {
                guestId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            }
            localStorage.setItem('guest_id', guestId);
            if (isNative) {
                await mobileStorage.setItem('guest_id', guestId);
            }
        }
        if (guestId) {
            config.headers['X-Guest-ID'] = guestId;
        }
    }

    // Visitor Opt-out Header
    if (localStorage.getItem('visitor_opt_out') === 'true') {
        config.headers['X-Visitor-Opt-Out'] = '1';
    }

    return config;
});

// Security fix M4: Auto-refresh access token on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retrying, attempt token refresh
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((newToken) => {
                    if (newToken && Capacitor.isNativePlatform()) {
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    }
                    return api(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                if (Capacitor.isNativePlatform()) {
                    const refreshToken = await mobileStorage.getRefreshToken();
                    const refreshRes = await api.post('/auth/refresh', refreshToken ? { refresh_token: refreshToken } : {});
                    const newAuthToken = refreshRes.data?.data?.auth_token || refreshRes.data?.auth_token;
                    const newRefreshToken = refreshRes.data?.data?.refresh_token || refreshRes.data?.refresh_token;
                    await mobileStorage.setTokens(newAuthToken, newRefreshToken);
                    if (newAuthToken) {
                        originalRequest.headers['Authorization'] = `Bearer ${newAuthToken}`;
                    }
                    processQueue(null, newAuthToken);
                    return api(originalRequest);
                } else {
                    await api.post('/auth/refresh');
                    processQueue(null);
                    return api(originalRequest);
                }
            } catch (refreshErr) {
                processQueue(refreshErr, null);
                // Refresh failed — user needs to re-login
                localStorage.removeItem('is_authenticated');
                if (Capacitor.isNativePlatform()) {
                    await mobileStorage.removeItem('is_authenticated');
                    await mobileStorage.clearTokens();
                }
                return Promise.reject(refreshErr);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Helper to map backend product to frontend format
const mapProduct = (p) => {
    // Parse JSON fields if they come as strings (safety check)
    let images = p.images || [];
    if (typeof images === 'string') {
        try { images = JSON.parse(images); } catch (e) { images = []; }
    }

    let sizes = p.sizes;
    if (typeof sizes === 'string') {
        try { sizes = JSON.parse(sizes); } catch (e) { sizes = []; }
    }

    let colors = p.colors;
    if (typeof colors === 'string') {
        try { colors = JSON.parse(colors); } catch (e) { colors = []; }
    }

    let variant_urls = p.variant_urls;
    if (typeof variant_urls === 'string') {
        try { variant_urls = JSON.parse(variant_urls); } catch (e) { variant_urls = []; }
    }

    // Use site_name from DB if available, else infer from URL
    let brand = p.site_name || "Marque Indépendante";
    if (!p.site_name && p.external_url) {
        try {
            const url = new URL(p.external_url);
            const hostname = url.hostname.replace('www.', '');
            let detectedBrand = hostname.split('.')[0];
            brand = detectedBrand.charAt(0).toUpperCase() + detectedBrand.slice(1);
        } catch (e) { /* ignore */ }
    }

    // Generate brand_slug from site_slug or brand name
    let brand_slug = p.site_slug || '';
    if (!brand_slug && brand) {
        brand_slug = brand.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, '-');
    }

    return {
        id: p.id,
        name: p.name,
        description: p.description,
        brand: brand,
        brand_slug: brand_slug,
        price: p.price,
        currency: p.currency,
        original_price: p.original_price,
        is_on_sale: p.is_on_sale,
        images: images,
        sizes: sizes,
        colors: colors,
        material: p.material,
        style: p.style,
        site_id: p.site_id,
        variant_group_id: p.variant_group_id,
        variant_urls: variant_urls,
        is_manually_edited: p.is_manually_edited,
        product_url: p.external_url,
        external_url: p.external_url,
        shopify_product_id: p.shopify_product_id,
        created_date: p.created_at || p.last_scraped_at,
        created_at: p.created_at,
        last_scraped_at: p.last_scraped_at,
        is_new: p.is_new
    };
};

export const apiClient = {
    brands: {
        getBySlug: async (slug, params = {}) => {
            try {
                const response = await api.get(`/scraped-products/brand/${slug}`, { params });
                const data = response.data;
                return {
                    ...data,
                    products: (data.products || []).map(mapProduct),
                    topProducts: (data.topProducts || []).map(mapProduct)
                };
            } catch (error) {
                if (error.response?.status === 404) return null;
                throw error;
            }
        },
        list: async () => {
            try {
                const response = await api.get('/scraped-products/brands/list');
                return response.data.brands || [];
            } catch (error) {
                return [];
            }
        }
    },
    auth: {
        me: async () => {
            try {
                const response = await api.get('/auth/me');
                // If /me succeeds, we are authenticated (e.g. page reload with valid refresh cookie / bearer token)
                localStorage.setItem('is_authenticated', 'true');
                if (Capacitor.isNativePlatform()) {
                    await mobileStorage.setItem('is_authenticated', 'true');
                }
                return response.data.data;
            } catch (error) {
                localStorage.removeItem('is_authenticated');
                if (Capacitor.isNativePlatform()) {
                    await mobileStorage.removeItem('is_authenticated');
                }
                throw error;
            }
        },
        login: async (credentials) => {
            const response = await api.post('/auth/login', credentials);
            const { user, auth_token, refresh_token } = response.data.data;
            // Security fix E3: On Web, httpOnly cookies handle auth. On Mobile, persist Bearer tokens natively.
            localStorage.setItem('is_authenticated', 'true');
            if (Capacitor.isNativePlatform()) {
                await mobileStorage.setTokens(
                    auth_token || response.data.auth_token,
                    refresh_token || response.data.refresh_token
                );
                await mobileStorage.setItem('is_authenticated', 'true');
            }

            // Sync Guest Interactions
            try {
                const guestInteractionsJson = localStorage.getItem('guest_interactions');
                if (guestInteractionsJson) {
                    const guestInteractions = JSON.parse(guestInteractionsJson);
                    if (guestInteractions.length > 0) {
                        await api.post('/interactions/batch', { interactions: guestInteractions });
                        localStorage.removeItem('guest_interactions');
                        if (Capacitor.isNativePlatform()) {
                            await mobileStorage.removeItem('guest_interactions');
                        }
                    }
                }
            } catch (syncError) {
                // Non-blocking error
            }

            return { user };
        },
        register: async (data) => {
            const response = await api.post('/auth/register', data);
            const { user, auth_token, refresh_token } = response.data.data;
            // Security fix E3: On Web, httpOnly cookies handle auth. On Mobile, persist Bearer tokens natively.
            localStorage.setItem('is_authenticated', 'true');
            if (Capacitor.isNativePlatform()) {
                await mobileStorage.setTokens(
                    auth_token || response.data.auth_token,
                    refresh_token || response.data.refresh_token
                );
                await mobileStorage.setItem('is_authenticated', 'true');
            }

            // Sync Guest Interactions
            try {
                const guestInteractionsJson = localStorage.getItem('guest_interactions');
                if (guestInteractionsJson) {
                    const guestInteractions = JSON.parse(guestInteractionsJson);
                    if (guestInteractions.length > 0) {
                        await api.post('/interactions/batch', { interactions: guestInteractions });
                        localStorage.removeItem('guest_interactions');
                        if (Capacitor.isNativePlatform()) {
                            await mobileStorage.removeItem('guest_interactions');
                        }
                    }
                }
            } catch (syncError) {
                // Non-blocking error
            }

            return { user };
        },
        verifyEmail: async (token) => {
            const response = await api.get(`/auth/verify-email?token=${token}`);
            return response.data;
        },
        logout: async () => {
            // Security fix E3: clear httpOnly cookies via server endpoint
            try {
                await api.post('/auth/logout');
            } catch (e) {
                // Best-effort server-side logout
            }
            // Clear auth state flag
            localStorage.removeItem('is_authenticated');
            // Clear local guest data
            localStorage.removeItem('guest_id');
            localStorage.removeItem('guest_interactions');
            localStorage.removeItem('guest_categories');
            localStorage.removeItem('guestCategories');
            if (Capacitor.isNativePlatform()) {
                await mobileStorage.clearTokens();
                await mobileStorage.removeItem('is_authenticated');
                await mobileStorage.removeItem('guest_id');
                await mobileStorage.removeItem('guest_interactions');
            }
            return Promise.resolve();
        },
        cancelDeletion: async (credentials) => {
            const response = await api.post('/auth/cancel-deletion', credentials);
            const { user, auth_token, refresh_token } = response.data.data;
            // Security fix E3: token handled via httpOnly cookies on Web, Bearer on Mobile
            localStorage.setItem('is_authenticated', 'true');
            if (Capacitor.isNativePlatform()) {
                await mobileStorage.setTokens(
                    auth_token || response.data.auth_token,
                    refresh_token || response.data.refresh_token
                );
                await mobileStorage.setItem('is_authenticated', 'true');
            }
            return { user };
        },
        resendVerification: async (data) => {
            const response = await api.post('/auth/resend-verification', data);
            return response.data;
        },
        scheduleDeletion: async () => {
            const response = await api.post('/auth/schedule-deletion');
            return response.data;
        },

        updateProfile: async (data) => {
            const response = await api.put('/auth/me', data);
            return response.data.data;
        },
        updateOnboarding: async (data) => {
            const response = await api.put('/auth/me/onboarding', data);
            return response.data.data;
        },
        requestPasswordReset: async ({ email }) => {
            const response = await api.post('/auth/request-password-reset', { email });
            return response.data;
        },
        resetPassword: async ({ token, password }) => {
            const response = await api.post('/auth/reset-password', { token, password });
            return response.data;
        },
        resetHistory: async () => {
            const response = await api.post('/auth/reset-history');
            return response.data;
        },
        restoreHistory: async () => {
            const response = await api.post('/auth/restore-history');
            return response.data;
        }
    },
    support: {
        contact: async (data) => {
            const response = await api.post('/contact', data);
            return response.data;
        }
    },
    notifications: {
        getPreferences: async () => {
            const response = await api.get('/notifications/preferences');
            return response.data.data;
        },
        updatePreferences: async (preferences) => {
            const response = await api.put('/notifications/preferences', preferences);
            return response.data.data;
        },
        getSubscriptions: async () => {
            const response = await api.get('/notifications/subscriptions');
            return response.data.data;
        },
        subscribe: async (siteId) => {
            const response = await api.post(`/notifications/subscribe/${siteId}`);
            return response.data;
        },
        unsubscribe: async (siteId) => {
            const response = await api.delete(`/notifications/subscribe/${siteId}`);
            return response.data;
        },
        testPush: async () => {
            const response = await api.post('/notifications/push/test');
            return response.data;
        }
    },
    unsubscribe: {
        verify: async (token) => {
            const response = await api.get('/unsubscribe/verify', { params: { token } });
            return response.data;
        },
        confirm: async (token, options = {}) => {
            const response = await api.post('/unsubscribe', { token, ...options });
            return response.data;
        },
        resubscribe: async (token, options = {}) => {
            const response = await api.post('/unsubscribe/resubscribe', { token, ...options });
            return response.data;
        }
    },
    entities: {
        Product: {
            list: async (sort, filters = {}) => {
                try {
                    // Fetch real products from the scraper database
                    // Pass filters as query params
                    const params = { sort, ...filters };
                    const response = await api.get('/scraped-products', { params });
                    const rawProducts = response.data.products || [];

                    // Map backend fields to frontend expectations
                    const mapped = rawProducts.map(mapProduct);
                    // Attach totalCount to the array (hacky but preserves API)
                    mapped.totalCount = response.data.totalCount;
                    return mapped;
                } catch (error) {
                    // console.error("Failed to fetch products:", error);
                    const empty = [];
                    empty.totalCount = 0;
                    return empty;
                }
            },
            showcase: async (itemsPerBrand = 4) => {
                try {
                    const response = await api.get('/scraped-products/showcase', { params: { items_per_brand: itemsPerBrand } });
                    const rawProducts = response.data.products || [];
                    const mapped = rawProducts.map(mapProduct);
                    mapped.totalCount = response.data.count;
                    return mapped;
                } catch (error) {
                    // console.error("Failed to fetch showcase:", error);
                    return [];
                }
            },
            hallOfFame: async () => {
                try {
                    const response = await api.get('/scraped-products/hall-of-fame');
                    return mapProduct(response.data.data);
                } catch (error) {
                    return null;
                }
            },
            get: async (id) => {
                try {
                    const response = await api.get(`/scraped-products/${id}`);
                    return mapProduct(response.data.product);
                } catch (error) {
                    // console.error(`Failed to fetch product ${id}:`, error);
                    return null;
                }
            },
            count: async () => {
                try {
                    const response = await api.get('/scraped-products/count');
                    return response.data.count || 0;
                } catch (error) {
                    // console.error("Failed to fetch product count:", error);
                    return 0;
                }
            },
            getMetadata: async () => {
                try {
                    const response = await api.get('/scraped-products/metadata');
                    return response.data;
                } catch (error) {
                    // console.error("Failed to fetch product metadata:", error);
                    return { brands: [], styles: [], maxPrice: 500 };
                }
            },
            variants: async (variantGroupId) => {
                if (!variantGroupId) return [];
                try {
                    // We don't have a direct "get variants" endpoint, but we can query by site or just rely on the fact that we might need a new endpoint.
                    // The backend /scraped-products doesn't filter by group yet.
                    // Let's assume we need to filtering on client for now or add endpoint.
                    // Adding endpoint is best.
                    // For now, let's try to fetch all products for the site? No, too heavy.
                    // Let's add a specific endpoint for variants.
                    const response = await api.get(`/scraped-products/variants/${variantGroupId}`);
                    return (response.data.variants || []).map(mapProduct);
                } catch (e) {
                    // console.warn("Failed to fetch variants", e);
                    return [];
                }
            }
        },
        Recommendation: {
            similar: async (productId, limit = 10) => {
                try {
                    const response = await api.get(`/recommendations/similar/${productId}?limit=${limit}`);
                    const rawProducts = response.data.products || [];
                    return rawProducts.map(mapProduct);
                } catch (error) {
                    return [];
                }
            },
            next: async () => {
                // Recommendation algorithm now supports guests via X-Guest-ID
                try {
                    const response = await api.get('/recommendations/next');
                    const p = response.data.product;
                    if (!p) return null;
                    // Map product
                    return mapProduct(p);
                } catch (error) {
                    // console.error("Failed to fetch next recommendation:", error);
                    return null;
                }
            },
            list: async (limit = 20) => {
                // Guest Support enabled
                try {
                    const response = await api.get(`/recommendations/list?limit=${limit}`);
                    const rawProducts = response.data.products || [];
                    return rawProducts.map(mapProduct);
                } catch (error) {
                    // console.error("Failed to fetch recommendations:", error);
                    return [];
                }
            }
        },
        UserInteraction: {
            // ... existing create/list methods ...
            create: async (data) => {
                /* Consent check removed: tracking is now considered strictly necessary for the service */

                const saveGuestInteraction = () => {
                    const guestInteractions = JSON.parse(localStorage.getItem('guest_interactions') || '[]');
                    const newInteraction = {
                        id: `guest_${Date.now()}`,
                        user_id: 'guest',
                        product_id: data.product_id,
                        action: data.action,
                        created_at: new Date().toISOString(),
                        product: data.product,
                        metadata: data.metadata || {}
                    };
                    guestInteractions.unshift(newInteraction);
                    const serialized = JSON.stringify(guestInteractions);
                    localStorage.setItem('guest_interactions', serialized);
                    if (Capacitor.isNativePlatform()) {
                        mobileStorage.setItem('guest_interactions', serialized);
                    }
                    return newInteraction;
                };

                // Try API first (Guest or Auth) to use algorithm
                // const token = localStorage.getItem('token'); 
                // We send interactions to API even for guests now

                try {
                    // Start: Use specific endpoints if applicable
                    if (data.action === 'view_produit' && data.duration_seconds) {
                        await api.post('/interactions/view-product', {
                            productId: data.product_id,
                            tempsPasseSecondes: data.duration_seconds
                        });
                        return { success: true };
                    }
                    if (data.action === 'expand_description') {
                        await api.post(`/interactions/expand-description/${data.product_id}`);
                        return { success: true };
                    }
                    if (data.action === 'visit_marque_site' && data.brand_id) {
                        await api.post(`/interactions/visit-brand/${data.brand_id}`);
                        return { success: true };
                    }
                    if (['like', 'dislike', 'swipe_right', 'swipe_left'].includes(data.action)) {
                        // Translate frontend naming to backend naming
                        let backendAction = data.action;
                        if (data.action === 'swipe_right') backendAction = 'like';
                        if (data.action === 'swipe_left') backendAction = 'dislike';

                        await api.post('/interactions/swipe', {
                            productId: data.product_id,
                            action: backendAction
                        });
                        return { success: true };
                    }
                    // End: Specific endpoints

                    const response = await api.post('/interactions', data);
                    return response.data.data;
                } catch (error) {
                    // Fallback to guest mode if API fails (e.g. invalid token)
                    // console.warn("API interaction failed, falling back to guest mode", error);
                    return saveGuestInteraction();
                }
            },
            delete: async (id) => {
                // Guest mode: delete from localStorage
                if (id.toString().startsWith('guest_')) {
                    const guestInteractions = JSON.parse(localStorage.getItem('guest_interactions') || '[]');
                    const filtered = guestInteractions.filter(i => i.id !== id);
                    const serialized = JSON.stringify(filtered);
                    localStorage.setItem('guest_interactions', serialized);
                    if (Capacitor.isNativePlatform()) {
                        mobileStorage.setItem('guest_interactions', serialized);
                    }
                    return { success: true };
                }

                // Auth mode
                try {
                    const response = await api.delete(`/interactions/${id}`);
                    return response.data;
                } catch (error) {
                    // console.warn("[Interactions] API delete failed", error);
                    return { success: false, error };
                }
            },
            list: async (filters = {}) => {
                const getGuestInteractions = () => {
                    const guestInteractions = JSON.parse(localStorage.getItem('guest_interactions') || '[]');
                    // console.log('[Interactions] Loading from localStorage, count:', guestInteractions.length);
                    // Filter locally for guest mode
                    return guestInteractions.filter(i => {
                        if (filters.productId && String(i.product_id) !== String(filters.productId)) return false;
                        if (filters.action) {
                            const allowed = filters.action.split(',');
                            if (!allowed.includes(i.action)) return false;
                        }
                        return true;
                    }).map(i => ({
                        ...i,
                        product: i.product || {
                            id: i.product_id,
                            name: 'Produit Inconnu',
                            price: 0,
                            images: [],
                            brand: 'Marque Inconnue'
                        }
                    }));
                };

                // Always try API first (works for both auth users and guests via X-Guest-ID header)
                try {
                    // console.log('[Interactions] Fetching from API...', filters);
                    const response = await api.get('/interactions', { params: filters });
                    // console.log('[Interactions] API returned', response.data.data?.length, 'interactions');

                    if (!response.data.data || response.data.data.length === 0) {
                        // API returned empty, maybe fall back to localStorage if any (ONLY IF GUEST? Or always?)
                        // If API returns empty because of filters, we shouldn't fallback to ALL local storage.
                        // Assuming Guest Mode API transparency, if API returns empty, it means empty.
                        // Fallback logic in original code was: if empty, try local. 
                        // But if we filter by productId and get 0, it means not liked. Falling back to ALL local interactions is wrong.
                        // Let's keep fallback only if we are in pure guest mode? 
                        // Actually, the backend should handle guest ID.
                        // Let's keep the fallback logic but apply filters to it too.
                        const local = getGuestInteractions();
                        if (local.length > 0 && (!response.data.data)) { // Only if API returned NOTHING (undefined/null), not just empty array
                            // console.log('[Interactions] API empty/null, using localStorage fallback');
                            return local;
                        }
                        if (response.data.data && response.data.data.length === 0) return []; // Empty result is valid
                        return local.length > 0 ? local : [];
                    }

                    return response.data.data.map(i => {
                        let images = [];
                        try {
                            images = typeof i.product_images === 'string' ? JSON.parse(i.product_images) : i.product_images;
                        } catch (e) { images = []; }

                        return {
                            ...i,
                            product: {
                                id: i.product_id,
                                name: i.product_name,
                                price: i.product_price,
                                currency: i.product_currency,
                                images: images || [],
                                brand: i.site_name || i.product_brand_fallback || 'Marque Indépendante'
                            }
                        };
                    });
                } catch (error) {
                    // Fallback to guest mode if API fails
                    // console.warn("[Interactions] API fetch failed, falling back to guest history", error);
                    return getGuestInteractions();
                }
            }
        },

        UserCategory: {
            list: async () => {
                const getGuestCategories = () => {
                    return JSON.parse(localStorage.getItem('guest_categories') || '[]');
                };

                // Always try API first (auth via httpOnly cookies)
                try {
                    const response = await api.get('/categories');
                    return response.data.data;
                } catch (error) {
                    // Not authenticated or API error, fall back to guest categories
                    return getGuestCategories();
                }
            },
            getSystemRules: async () => {
                const response = await api.get('/categories/rules');
                return response.data.rules;
            },
            create: async (data) => {
                const saveGuestCategory = () => {
                    const guestCategories = JSON.parse(localStorage.getItem('guest_categories') || '[]');
                    const newCategory = {
                        id: `guest_${Date.now()}`,
                        user_id: 'guest',
                        product_id: data.product_id,
                        category_name: data.category_name,
                        created_at: new Date().toISOString()
                    };
                    guestCategories.push(newCategory);
                    localStorage.setItem('guest_categories', JSON.stringify(guestCategories));
                    return newCategory;
                };

                // Always try API first (auth via httpOnly cookies)
                try {
                    const response = await api.post('/categories', data);
                    return response.data.data;
                } catch (error) {
                    // Not authenticated or API error, fall back to guest mode
                    return saveGuestCategory();
                }
            },
            update: async (id, data) => {
                // Guest mode: update in localStorage
                if (id.toString().startsWith('guest_')) {
                    const guestCategories = JSON.parse(localStorage.getItem('guest_categories') || '[]');
                    const idx = guestCategories.findIndex(c => c.id === id);
                    if (idx !== -1) {
                        guestCategories[idx] = { ...guestCategories[idx], ...data };
                        localStorage.setItem('guest_categories', JSON.stringify(guestCategories));
                    }
                    return { success: true };
                }

                const response = await api.put(`/categories/${id}`, data);
                return response.data;
            },
            delete: async (id) => {
                // Guest mode: delete from localStorage
                if (id.toString().startsWith('guest_')) {
                    const guestCategories = JSON.parse(localStorage.getItem('guest_categories') || '[]');
                    const filtered = guestCategories.filter(c => c.id !== id);
                    localStorage.setItem('guest_categories', JSON.stringify(filtered));
                    return { success: true };
                }

                const response = await api.delete(`/categories/${id}`);
                return response.data;
            }
        }
    },

    // Admin API
    admin: {
        getRules: async () => {
            const response = await api.get('/admin/categories');
            return response.data.rules;
        },
        getAlgorithmConfig: async () => {
            const response = await api.get('/admin/algorithm-config');
            return response.data.data;
        },
        saveAlgorithmConfig: async (config) => {
            const response = await api.put('/admin/algorithm-config', config);
            return response.data.data;
        },
        getStats: async () => {
            const response = await api.get('/stats/global');
            return response.data.data;
        },
        getTopProducts: async (period = 'all') => {
            const response = await api.get('/stats/products/top', { params: { period } });
            return response.data.data;
        },
        getTopBrands: async (period = 'all') => {
            const response = await api.get('/stats/brands', { params: { period } });
            return response.data.data;
        },
        getCategoriesStats: async () => {
            const response = await api.get('/stats/categories');
            return response.data.data;
        },
        getInteractionStats: async () => {
            const response = await api.get('/stats/interactions/types');
            return response.data.data;
        },
        getActivityGraph: async () => {
            const response = await api.get('/stats/activity-graph');
            return response.data.data;
        },
        getTopSearches: async () => {
            const response = await api.get('/stats/search/top');
            return response.data.data;
        },
        getHourlyActivity: async (period = '30d') => {
            const response = await api.get('/stats/activity-hourly', { params: { period } });
            return response.data.data;
        },
        getPricingStats: async () => {
            const response = await api.get('/stats/preferences/pricing');
            return response.data.data;
        },
        getDeviceStats: async () => {
            const response = await api.get('/stats/devices');
            return response.data.data;
        },
        getProductsEngagement: async () => {
            const response = await api.get('/stats/products/engagement');
            return response.data.data;
        },
        getUnusedProducts: async () => {
            const response = await api.get('/stats/products/unused');
            return response.data.data;
        },
        resetStats: async () => {
            const response = await api.delete('/stats/reset');
            return response.data;
        },
        getDailyVisitors: async () => {
            const response = await api.get('/stats/visitors/daily');
            return response.data.data;
        },
        getDailySignups: async () => {
            const response = await api.get('/stats/users/daily');
            return response.data.data;
        },
        getRetentionStats: async () => {
            const response = await api.get('/stats/retention');
            return response.data.data;
        },
        getSessionMetrics: async (period = 'all') => {
            const response = await api.get('/stats/session-metrics', { params: { period } });
            return response.data.data;
        },
        getTopClicks: async (period = 'all') => {
            const response = await api.get('/stats/clicks/top', { params: { period } });
            return response.data.data;
        },
        getShopperStats: async () => {
            const response = await api.get('/stats/shopper');
            return response.data.data;
        },
        // Scraper Admin APIs
        getScraperJobs: async () => {
            const response = await api.get('/scraper/jobs');
            return response.data;
        },
        getScraperStats: async () => {
            const response = await api.get('/scraper/stats');
            return response.data;
        },
        addScraperJob: async (brandUrl) => {
            const response = await api.post('/scraper/scrape', { brandUrl });
            return response.data;
        },

        // Administrator Categories
        getCategoriesRules: async () => {
            const response = await api.get('/admin/categories');
            return response.data.rules;
        },
        getUnmappedStyles: async () => {
            const response = await api.get('/admin/categories/unmapped');
            return response.data.styles;
        },
        createCategoryRule: async (data) => {
            const response = await api.post('/admin/categories', data);
            return response.data.rule;
        },
        updateCategoryRule: async (id, data) => {
            const response = await api.put(`/admin/categories/${id}`, data);
            return response.data.rule;
        },
        deleteCategoryRule: async (id) => {
            const response = await api.delete(`/admin/categories/${id}`);
            return response.data;
        },
        seedCategoryRules: async () => {
            const response = await api.post('/admin/categories/seed');
            return response.data;
        },
        reorderCategoryRules: async (orderedIds) => {
            const response = await api.put('/admin/categories/reorder', { orderedIds });
            return response.data;
        },

        // Administrator Colors
        getColorRules: async () => {
            const response = await api.get('/admin/colors');
            return response.data.rules;
        },
        getUnmappedColors: async () => {
            const response = await api.get('/admin/colors/unmapped');
            return response.data.colors;
        },
        createColorRule: async (data) => {
            const response = await api.post('/admin/colors', data);
            return response.data.rule;
        },
        updateColorRule: async (id, data) => {
            const response = await api.put(`/admin/colors/${id}`, data);
            return response.data.rule;
        },
        deleteColorRule: async (id) => {
            const response = await api.delete(`/admin/colors/${id}`);
            return response.data;
        },
        seedColorRules: async () => {
            const response = await api.post('/admin/colors/seed');
            return response.data;
        },

        // Global Settings
        getSettings: async () => {
            const response = await api.get('/admin/settings');
            return response.data.data;
        },
        updateSettings: async (key, value) => {
            const response = await api.put('/admin/settings', { key, value });
            return response.data;
        },
        // Scraper Sites
        getScraperSites: async (activeOnly = false) => {
            const response = await api.get('/scraper-sites', { params: { activeOnly } });
            return response.data;
        },
        createScraperSite: async (data) => {
            const response = await api.post('/scraper-sites', data);
            return response.data;
        },
        updateScraperSite: async (id, data) => {
            const response = await api.put(`/scraper-sites/${id}`, data);
            return response.data;
        },
        deleteScraperSite: async (id) => {
            const response = await api.delete(`/scraper-sites/${id}`);
            return response.data;
        },
        triggerScrapeSite: async (id) => {
            const response = await api.post(`/scraper-sites/${id}/scrape-now`);
            return response.data;
        },

        // Poster Studio
        getPosterData: async () => {
            const response = await api.get('/admin/posters/data');
            return response.data;
        },
        generateHeaderPoster: async (part) => {
            const response = await api.post('/admin/posters/generate-header', { part }, { responseType: 'blob' });
            return response.data; // This will be the Blob
        },
        generateBrandPoster: async (slug, productIds, showNextArrow = false, customDescription = null) => {
            const response = await api.post('/admin/posters/generate-brand', { slug, productIds, showNextArrow, customDescription }, { responseType: 'blob' });
            return response.data; // This will be the Blob
        },
        generateAllPosters: async () => {
            const response = await api.post('/admin/posters/generate-all');
            return response.data;
        }
    },
    shopper: {
        getSessions: async () => {
            const response = await api.get('/shopper/sessions');
            return response.data;
        },
        getSession: async (id) => {
            const response = await api.get(`/shopper/sessions/${id}`);
            return response.data;
        },
        deleteSession: async (id) => {
            const response = await api.delete(`/shopper/sessions/${id}`);
            return response.data;
        },
        ask: async (message, history) => {
            const response = await api.post('/shopper/ask', { message, history });
            return response.data;
        },
        saveSession: async (id, payload) => {
            const url = id ? `/shopper/sessions/${id}` : '/shopper/sessions';
            const response = await api.post(url, payload);
            return response.data;
        }
    }
};

export { api };
